import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-side-by-side';

const GEOJSON_API_URL = 'https://luma-flood-algo-be.onrender.com/get-flood-mask';

const comparisonData = [
  { location: 'Mokwa, Niger', temperature: '23°C', humidity: '60%', risk: 'High' },
  { location: 'Ajah, Lagos', temperature: '19°C', humidity: '80%', risk: 'Medium' },
  { location: 'Idah, Kogi', temperature: '28°C', humidity: '75%', risk: 'High' },
];

export function MapSection() {
  const mapRef = useRef(null);
  const floodPaneRef = useRef(null);
  const floodLayerRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (mapRef.current) return;

    const map = L.map('slider-map', {
      center: [9, 8],
      zoom: 6,
      zoomControl: false,
    });

    mapRef.current = map;

    // Tile layers
    const beforeLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png');
    const afterLayer = L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png');
    beforeLayer.addTo(map);
    afterLayer.addTo(map);

    // Side-by-side control
    const sideBySide = L.control.sideBySide(beforeLayer, afterLayer).addTo(map);

    // Custom pane for GeoJSON on top
    const paneName = 'floodPane';
    map.createPane(paneName);
    const pane = map.getPane(paneName);
    floodPaneRef.current = pane;

    // Style pane to ensure it's on top
    if (pane) {
      pane.style.zIndex = 650; // higher than tile layers
      pane.style.pointerEvents = 'none'; // let interaction pass through
      pane.style.overflow = 'hidden';
    }

    // Fetch flood mask
    // fetch(GEOJSON_API_URL)
    //   .then((res) => res.json())
    //   .then((data) => {
    //     const layer = L.geoJSON(data.flood_geojson, {
    //       pane: paneName,
    //       style: {
    //         color: 'blue',
    //         weight: 1,
    //         fillColor: 'blue',
    //         fillOpacity: 0.3,
    //       },
    //     });

    //     layer.addTo(map);
    //     floodLayerRef.current = layer;

    //     map.fitBounds(layer.getBounds(), { maxZoom: 20 });
    //   })
    //   .catch((err) => console.error('Failed to load flood GeoJSON', err))
    //   .finally(() => {
    //     setLoading(false);
    //   });
    fetch(GEOJSON_API_URL)
  .then((res) => res.json())
  .then((data) => {
    const layer = L.geoJSON(data.flood_geojson, {
      pane: 'overlayPane',
      style: {
        color: 'blue',
        weight: 1,
        fillColor: 'blue',
        fillOpacity: 0.3,
      },
    });

    layer.addTo(map);
    floodLayerRef.current = layer;

    // Clip the overlay pane to only the "after" side
    const divider = document.querySelector('.leaflet-sbs-divider');
    const container = map.getContainer();

    const updateClip = () => {
      const mapRect = container.getBoundingClientRect();
      const dividerX = divider?.getBoundingClientRect().left - mapRect.left || 0;

      const pane = map.getPane('overlayPane');
      if (pane) {
        pane.style.clipPath = `inset(0 0 0 ${dividerX}px)`;
        pane.style.webkitClipPath = `inset(0 0 0 ${dividerX}px)`;
      }
    };

    const observer = new MutationObserver(updateClip);
    observer.observe(document.body, { attributes: true, subtree: true });
    map.on('move resize zoom', updateClip);
    updateClip(); // trigger once immediately

    map.fitBounds(layer.getBounds(), { maxZoom: 12 });
  })
  .catch((err) => console.error('Failed to load flood GeoJSON', err))
  .finally(() => setLoading(false));


    // Dynamically resize pane to match after-side only
    const adjustFloodMask = () => {
      const divider = sideBySide._divider;
      const container = map.getContainer();
      if (!divider || !floodPaneRef.current) return;

      const mapRect = container.getBoundingClientRect();
      const dividerX = divider.getBoundingClientRect().left - mapRect.left;

      floodPaneRef.current.style.clipPath = `inset(0 0 0 ${dividerX}px)`;
      floodPaneRef.current.style.webkitClipPath = `inset(0 0 0 ${dividerX}px)`;
    };

    const observer = new MutationObserver(adjustFloodMask);
    observer.observe(document.body, { attributes: true, subtree: true });

    map.on('move zoom resize', adjustFloodMask);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section id="map-section" className="py-16 px-6 bg-white relative">
      {/* Spinner overlay */}
      {loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80 text-gray-700 font-medium text-lg">
          Damage assessment model is loading...
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl mb-4">Satellite Weather Mapping</h2>
          <p className="text-gray-600 text-lg">Real-time before/after satellite comparison</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div
              className="rounded-lg shadow-sm border overflow-hidden relative"
              style={{ height: '384px' }}
            >
              <div id="slider-map" className="w-full h-full" />
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white p-4 rounded-lg shadow-sm border h-full">
              <div className="mb-4">
                <h3 className="text-xl font-semibold">Regional Comparison</h3>
              </div>

              <div className="space-y-4">
                {comparisonData.map((item, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-sm font-medium">{item.location}</h4>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          item.risk === 'Low'
                            ? 'bg-green-100 text-green-800'
                            : item.risk === 'Medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {item.risk}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div>Temp: {item.temperature}</div>
                      <div>Humidity: {item.humidity}</div>
                    </div>
                  </div>
                ))}

                <div className="mt-6">
                  <h4 className="mb-3 font-semibold">Flood Alerts</h4>
                  <div className="space-y-2 text-sm">
                    <div className="p-2 bg-yellow-50 border-l-4 border-yellow-400">
                      <div className="text-yellow-800">High Rainfall Alert</div>
                      <div className="text-yellow-600 text-xs">Expected time - 15:30</div>
                    </div>
                    <div className="p-2 bg-blue-50 border-l-4 border-blue-400">
                      <div className="text-blue-800">Elevated River Level</div>
                      <div className="text-blue-600 text-xs">Increase - 2mm</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}




// import { useEffect, useRef, useState } from 'react';
// import L from 'leaflet';
// import 'leaflet/dist/leaflet.css';
// import 'leaflet-side-by-side';

// const GEOJSON_API_URL = 'https://luma-flood-algo-be.onrender.com/get-flood-mask';

// const comparisonData = [
//   { location: 'Mokwa, Niger', temperature: '23°C', humidity: '60%', risk: 'High' },
//   { location: 'Ajah, Lagos', temperature: '19°C', humidity: '80%', risk: 'Medium' },
//   { location: 'Idah, Kogi', temperature: '28°C', humidity: '75%', risk: 'High' },
// ];

// export function MapSection() {
//   const mapRef = useRef(null);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     if (mapRef.current) return;

//     const map = L.map('slider-map', {
//       center: [9, 20],
//       zoom: 4,
//       zoomControl: false,
//     });

//     mapRef.current = map;

//     const beforeLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png');
//     const afterTileLayer = L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png');
//     const afterGroup = L.layerGroup([afterTileLayer]);

//     beforeLayer.addTo(map);
//     afterGroup.addTo(map);

//     const sideBySide = L.control.sideBySide(beforeLayer, afterTileLayer).addTo(map);

//     fetch(GEOJSON_API_URL)
//       .then((res) => res.json())
//       .then((data) => {
//         const floodGeoJson = data.flood_geojson;

//         const floodLayer = L.geoJSON(floodGeoJson, {
//           style: {
//             color: 'blue',
//             weight: 1,
//             fillColor: 'blue',
//             fillOpacity: 0.3,
//           },
//         });

//         afterGroup.addLayer(floodLayer);
//         map.fitBounds(floodLayer.getBounds(), { maxZoom: 12 });
//       })
//       .catch((err) => console.error('Failed to load flood GeoJSON', err))
//       .finally(() => setLoading(false));
//   }, []);

//   return (
//     <section id="map-section" className="py-16 px-6 bg-white relative">
//       {loading && (
//         <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 text-gray-700 font-medium text-lg">
//           Damage assessment model is loading...
//         </div>
//       )}

//       <div className="max-w-7xl mx-auto">
//         <div className="text-center mb-12">
//           <h2 className="text-4xl mb-4">Satellite Weather Mapping</h2>
//           <p className="text-gray-600 text-lg">Real-time before/after satellite comparison</p>
//         </div>

//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
//           {/* Map */}
//           <div className="lg:col-span-2">
//             <div className="rounded-lg shadow-sm border overflow-hidden relative" style={{ height: '384px' }}>
//               <div id="slider-map" className="w-full h-full" />
//             </div>
//           </div>

//           {/* Sidebar */}
//           <div className="lg:col-span-1">
//             <div className="bg-white p-4 rounded-lg shadow-sm border h-full">
//               <div className="mb-4">
//                 <h3 className="text-xl font-semibold">Regional Comparison</h3>
//               </div>

//               <div className="space-y-4">
//                 {comparisonData.map((item, index) => (
//                   <div key={index} className="p-3 bg-gray-50 rounded-lg">
//                     <div className="flex justify-between items-start mb-2">
//                       <h4 className="text-sm font-medium">{item.location}</h4>
//                       <span
//                         className={`text-xs px-2 py-1 rounded-full ${
//                           item.risk === 'Low'
//                             ? 'bg-green-100 text-green-800'
//                             : item.risk === 'Medium'
//                             ? 'bg-yellow-100 text-yellow-800'
//                             : 'bg-red-100 text-red-800'
//                         }`}
//                       >
//                         {item.risk}
//                       </span>
//                     </div>
//                     <div className="text-xs text-gray-600 space-y-1">
//                       <div>Temp: {item.temperature}</div>
//                       <div>Humidity: {item.humidity}</div>
//                     </div>
//                   </div>
//                 ))}

//                 <div className="mt-6">
//                   <h4 className="mb-3 font-semibold">Flood Alerts</h4>
//                   <div className="space-y-2 text-sm">
//                     <div className="p-2 bg-yellow-50 border-l-4 border-yellow-400">
//                       <div className="text-yellow-800">High Rainfall Alert</div>
//                       <div className="text-yellow-600 text-xs">Expected time - 15:30</div>
//                     </div>
//                     <div className="p-2 bg-blue-50 border-l-4 border-blue-400">
//                       <div className="text-blue-800">Elevated River Level</div>
//                       <div className="text-blue-600 text-xs">Increase - 2mm</div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// }


///////////////////////////////////////////////////////////////////////////////////

// import { useEffect, useRef } from 'react';
// import L from 'leaflet';
// import 'leaflet/dist/leaflet.css';
// import 'leaflet-side-by-side';

// // Replace with your actual backend GeoJSON endpoint
// const GEOJSON_API_URL = 'https://luma-flood-algo-be.onrender.com/get-flood-mask';

// const comparisonData = [
//   { location: 'Mokwa, Niger', temperature: '23°C', humidity: '60%', risk: 'High' },
//   { location: 'Ajah, Lagos', temperature: '19°C', humidity: '80%', risk: 'Medium' },
//   { location: 'Idah, Kogi', temperature: '28°C', humidity: '75%', risk: 'High' },
// ];

// export function MapSection() {
//   const mapRef = useRef(null);

//   useEffect(() => {
//     if (mapRef.current) return;

//     const map = L.map('slider-map', {
//       center: [9, 20],
//       zoom: 4,
//       zoomControl: false,
//     });

//     mapRef.current = map;

//     const beforeLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png');
//     const afterLayer = L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png');

//     beforeLayer.addTo(map);
//     afterLayer.addTo(map);

//     const sideBySide = L.control.sideBySide(beforeLayer, afterLayer).addTo(map);

//     // 🚨 Fetch and add flood mask GeoJSON
//     fetch(GEOJSON_API_URL)
//       .then((res) => res.json())
//       .then((geojson) => {
//         const floodLayer = L.geoJSON(geojson, {
//           style: {
//             color: 'blue',
//             weight: 1,
//             fillColor: 'blue',
//             fillOpacity: 0.3,
//           },
//         }).addTo(map);

//         map.fitBounds(floodLayer.getBounds(), { maxZoom: 12 });
//       })
//       .catch((err) => console.error('Failed to load flood GeoJSON', err));
//   }, []);

//   return (
//     <section id="map-section" className="py-16 px-6 bg-white">
//       <div className="max-w-7xl mx-auto">
//         <div className="text-center mb-12">
//           <h2 className="text-4xl mb-4">Satellite Weather Mapping</h2>
//           <p className="text-gray-600 text-lg">Real-time before/after satellite comparison</p>
//         </div>

//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
//           {/* Map display */}
//           <div className="lg:col-span-2">
//             <div className="rounded-lg shadow-sm border overflow-hidden" style={{ height: '384px' }}>
//               <div id="slider-map" className="w-full h-full" />
//             </div>
//           </div>

//           {/* Sidebar */}
//           <div className="lg:col-span-1">
//             <div className="bg-white p-4 rounded-lg shadow-sm border h-full">
//               <div className="mb-4">
//                 <h3 className="text-xl font-semibold">Regional Comparison</h3>
//               </div>

//               <div className="space-y-4">
//                 {comparisonData.map((item, index) => (
//                   <div key={index} className="p-3 bg-gray-50 rounded-lg">
//                     <div className="flex justify-between items-start mb-2">
//                       <h4 className="text-sm font-medium">{item.location}</h4>
//                       <span
//                         className={`text-xs px-2 py-1 rounded-full ${
//                           item.risk === 'Low'
//                             ? 'bg-green-100 text-green-800'
//                             : item.risk === 'Medium'
//                             ? 'bg-yellow-100 text-yellow-800'
//                             : 'bg-red-100 text-red-800'
//                         }`}
//                       >
//                         {item.risk}
//                       </span>
//                     </div>
//                     <div className="text-xs text-gray-600 space-y-1">
//                       <div>Temp: {item.temperature}</div>
//                       <div>Humidity: {item.humidity}</div>
//                     </div>
//                   </div>
//                 ))}

//                 <div className="mt-6">
//                   <h4 className="mb-3 font-semibold">Flood Alerts</h4>
//                   <div className="space-y-2 text-sm">
//                     <div className="p-2 bg-yellow-50 border-l-4 border-yellow-400">
//                       <div className="text-yellow-800">High Rainfall Alert</div>
//                       <div className="text-yellow-600 text-xs">Expected time - 15:30</div>
//                     </div>
//                     <div className="p-2 bg-blue-50 border-l-4 border-blue-400">
//                       <div className="text-blue-800">Elevated River Level</div>
//                       <div className="text-blue-600 text-xs">Increase - 2mm</div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// }

////////////////////////////////////////////////////////////////////////////////////////////////////////////

// import { useEffect, useRef } from 'react';
// import L from 'leaflet';
// import 'leaflet/dist/leaflet.css';
// import 'leaflet-side-by-side';


// // Dummy data for demonstration (remove if already defined elsewhere)
// const comparisonData = [
//   { location: 'Mokwa, Niger', temperature: '23°C', humidity: '60%', risk: 'High' },
//   { location: 'Ajah, Lagos', temperature: '19°C', humidity: '80%', risk: 'Medium'},
//   { location: 'Idah, Kogi', temperature: '28°C', humidity: '75%', risk: 'High' },
 
// ];

// export function MapSection() {
//   const mapRef = useRef(null);

//   useEffect(() => {
//     if (mapRef.current) return;

//     const map = L.map('slider-map', {
//       center: [9, 20],
//       zoom: 4,
//       zoomControl: false,
//     });

//     mapRef.current = map;

//     const beforeLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png');
//     const afterLayer = L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png');

//     const sideBySide = L.control.sideBySide(beforeLayer, afterLayer);
//     beforeLayer.addTo(map);
//     afterLayer.addTo(map);
//     sideBySide.addTo(map);
//   }, []);

//   return (
//     <section id="map-section" className="py-16 px-6 bg-white">
//       <div className="max-w-7xl mx-auto">
//         <div className="text-center mb-12">
//           <h2 className="text-4xl mb-4">Satellite Weather Mapping</h2>
//           <p className="text-gray-600 text-lg">
//             Real-time before/after satellite comparison
//           </p>
//         </div>

//         {/* Responsive layout */}
//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
//           {/* Map on the left (2/3 width on large screens) */}
//           <div className="lg:col-span-2">
//             <div className="rounded-lg shadow-sm border overflow-hidden" style={{ height: '384px' }}>
//               <div id="slider-map" className="w-full h-full" />
//             </div>
//           </div>

//           {/* Comparison Sidebar (1/3 width on large screens) */}
//           <div className="lg:col-span-1">
//             <div className="bg-white p-4 rounded-lg shadow-sm border h-full">
//               <div className="mb-4">
//                 <h3 className="text-xl font-semibold">Regional Comparison</h3>
//               </div>

//               <div className="space-y-4">
//                 {comparisonData.map((item, index) => (
//                   <div key={index} className="p-3 bg-gray-50 rounded-lg">
//                     <div className="flex justify-between items-start mb-2">
//                       <h4 className="text-sm font-medium">{item.location}</h4>
//                       <span
//                         className={`text-xs px-2 py-1 rounded-full ${
//                           item.risk === 'Low'
//                             ? 'bg-green-100 text-green-800'
//                             : item.risk === 'Medium'
//                             ? 'bg-yellow-100 text-yellow-800'
//                             : 'bg-red-100 text-red-800'
//                         }`}
//                       >
//                         {item.risk}
//                       </span>
//                     </div>
//                     <div className="text-xs text-gray-600 space-y-1">
//                       <div>Temp: {item.temperature}</div>
//                       <div>Humidity: {item.humidity}</div>
//                     </div>
//                   </div>
//                 ))}

//                 {/* Alerts */}
//                 <div className="mt-6">
//                   <h4 className="mb-3 font-semibold">Flood Alerts</h4>
//                   <div className="space-y-2 text-sm">
//                     <div className="p-2 bg-yellow-50 border-l-4 border-yellow-400">
//                       <div className="text-yellow-800">High Rainfall Alert</div>
//                       <div className="text-yellow-600 text-xs">Expected time - 15:30</div>
//                     </div>
//                     <div className="p-2 bg-blue-50 border-l-4 border-blue-400">
//                       <div className="text-blue-800">Elevated River Level</div>
//                       <div className="text-blue-600 text-xs">Increase - 2mm</div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// }

